import React, { useEffect, useState } from 'react';
import HAppRoutes from '../../../../app/features/routes/helpers/HAppRoutes';
import useAuthOktaUser from '../../../../app/hooks/useAuthOktaUser';
import AuthRoutes from '../../AuthRoutes';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { Box, Button, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import styleVariables from '../../../../app/styles/styleVariables';
import AppLoaderContent from '../../../../app/features/loader/content/AppLoaderContent';
import useAuthOktaUserActivation from './hooks/useAuthOktaUserActivation';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import FAuthOktaUserActivationResend from '../okta-user-activation-resend/FAuthOktaUserActivationResend';
import langAuthOktaUserActivation from './langs/langAuthOktaUserActivation';
import { useTranslation } from 'react-i18next';
import { useAppLang } from '../../../../app/features/lang/providers/AppLangProvider';

type FAuthOktaUserActivationProps = {
	activationToken: string;
};

interface IAuthPasswordCreate {
	newPassword: string;
	newPasswordConfirmation: string;
}

const FAuthOktaUserActivation = (props: FAuthOktaUserActivationProps) => {
	const { activationToken } = props;
	const navigate = useNavigate();
	const { t } = useTranslation();
	const authOktaUser = useAuthOktaUser();
	const appLang = useAppLang();
	const { mutate: userActivate, data, error, isLoading } = useAuthOktaUserActivation();
	const [stateToken, setStateToken] = useState<string | null | undefined>(undefined);
	const [profile, setProfile] = useState<{ locale?: string; firstName?: string; lastName?: string } | undefined>(
		undefined,
	);

	const [isActivate, setIsActivate] = useState(false);

	useEffect(() => {
		if (stateToken === undefined && activationToken) {
			userActivate(activationToken);
		}
	}, [activationToken, stateToken, userActivate]);

	useEffect(() => {
		if (data && !isLoading) {
			if (data) {
				setProfile(data.data._embedded.user.profile);
				if (data.data._embedded.user.profile.login) {
					localStorage.setItem('authLogin', data.data._embedded.user.profile.login);
				} else {
					localStorage.removeItem('authLogin');
				}
				const stateToken = data.data.stateToken;
				setStateToken(stateToken);
			}
		}
		if (error) {
			setStateToken(null);
		}
	}, [stateToken, data, error, isLoading]);

	useEffect(() => {
		if (
			appLang &&
			appLang.language &&
			profile &&
			profile.locale &&
			profile.locale.slice(0, 2).toLowerCase() !== appLang.language
		) {
			appLang.changeLanguage(profile.locale.slice(0, 2).toLowerCase());
		}
	}, [appLang, profile]);

	const sxTextField = {
		'& .MuiInputBase-root': {
			border: '1px solid #EEE',
		},
	};

	const schema = yup
		.object({
			newPassword: yup
				.string()
				.required(t(langAuthOktaUserActivation.field_error_password_required.tag))
				.min(8, t(langAuthOktaUserActivation.field_error_password_too_short.tag))
				.matches(
					/(.*[a-z].*)/,
					t(langAuthOktaUserActivation.field_error_password_must_contain_one_lowercase.tag),
				)
				.matches(
					/(.*[A-Z].*)/,
					t(langAuthOktaUserActivation.field_error_password_must_contain_one_uppercase.tag),
				)
				.matches(/(.*\d.*)/, t(langAuthOktaUserActivation.field_error_password_must_contain_one_number.tag)),
			newPasswordConfirmation: yup
				.mixed()
				.test(
					'match',
					t(langAuthOktaUserActivation.field_error_passwords_do_not_match.tag),
					function (password) {
						return password === this.parent.newPassword;
					},
				)
				.required(t(langAuthOktaUserActivation.field_error_password_confirm_required.tag)),
		})
		.required();

	const {
		handleSubmit,
		control,
		setError,
		reset,
		formState: { errors },
	} = useForm<IAuthPasswordCreate>({ resolver: yupResolver(schema) });

	const onSubmit: SubmitHandler<IAuthPasswordCreate> = (data) => {
		authOktaUser
			.signIn({ stateToken: stateToken ?? undefined })
			.then((transaction) => {
				// `resetPassword` is exposed by Okta's auth-state machine on the
				// password-recovery / activation transaction step. Not in the public
				// type, so we narrow structurally here.
				type ResetPwdTransaction = {
					resetPassword?: (args: { newPassword: string }) => Promise<unknown>;
				};
				type OktaErrorCause = { errorSummary: string };
				type OktaError = { errorCauses?: OktaErrorCause[] };
				(transaction as ResetPwdTransaction)
					.resetPassword?.({ newPassword: data.newPassword })
					.then(() => {
						setIsActivate(true);
						reset();
					})
					.catch((e: OktaError) => {
						const summary = e.errorCauses?.[0]?.errorSummary ?? '';
						setError('newPassword', { type: 'custom', message: summary });
					});
			})
			.catch(() => {
				setStateToken(null);
			});
	};

	return (
		<>
			{isActivate ? (
				<>
					<Typography variant="body2" gutterBottom sx={{ textAlign: 'center', marginBottom: '30px' }}>
						{t(langAuthOktaUserActivation.text_success.tag)}
					</Typography>
					<Button
						variant="contained"
						color="secondary"
						type="button"
						onClick={() => navigate(HAppRoutes.getPath(AuthRoutes.login.tag))}
					>
						{t(langAuthOktaUserActivation.btn_login.tag)}
					</Button>
				</>
			) : (
				<>
					{stateToken ? (
						<form onSubmit={handleSubmit(onSubmit)}>
							{profile ? (
								<Typography
									variant="body2"
									gutterBottom
									sx={{ textAlign: 'left', fontWeight: 600, marginBottom: '15px' }}
								>
									{t(langAuthOktaUserActivation.text_hello.tag, {
										firstName: profile.firstName,
										lastName: profile.lastName,
									})}
								</Typography>
							) : (
								''
							)}
							<Typography variant="body2" gutterBottom sx={{ textAlign: 'left', marginBottom: '15px' }}>
								{t(langAuthOktaUserActivation.text_presentation.tag)}
							</Typography>
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<Controller
										name="newPassword"
										control={control}
										defaultValue=""
										render={({ field }) => (
											<TextField
												fullWidth
												sx={sxTextField}
												label={t(langAuthOktaUserActivation.label_password.tag)}
												variant="outlined"
												type="password"
												size="medium"
												autoComplete="off"
												{...field}
												error={!!errors.newPassword}
												helperText={
													errors?.newPassword?.message ??
													t(langAuthOktaUserActivation.label_password_helpertext.tag)
												}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<AppCustomIcon
																tag="auth_password"
																sx={{ color: styleVariables.primaryColor }}
															/>
														</InputAdornment>
													),
												}}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Controller
										name="newPasswordConfirmation"
										control={control}
										defaultValue=""
										render={({ field }) => (
											<TextField
												fullWidth
												sx={sxTextField}
												label={t(langAuthOktaUserActivation.label_password_confirmation.tag)}
												variant="outlined"
												type="password"
												size="medium"
												autoComplete="off"
												{...field}
												error={!!errors.newPasswordConfirmation}
												helperText={errors?.newPasswordConfirmation?.message}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<AppCustomIcon
																tag="auth_password"
																sx={{ color: styleVariables.primaryColor }}
															/>
														</InputAdornment>
													),
												}}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Box style={{ textAlign: 'center' }}>
										<Button
											type="submit"
											variant="contained"
											color="secondary"
											sx={{ minWidth: 160, fontWeight: 600 }}
											endIcon={<AppCustomIcon tag="arrow-right" />}
										>
											{t(langAuthOktaUserActivation.btn_save.tag)}
										</Button>
									</Box>
								</Grid>
							</Grid>
						</form>
					) : stateToken === null ? (
						<FAuthOktaUserActivationResend />
					) : (
						<AppLoaderContent sx={{ height: '200px', minHeight: '200px' }} />
					)}
				</>
			)}
		</>
	);
};

export default FAuthOktaUserActivation;
